import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, onAddToCart, onQuickView }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    
    try {
      await onAddToCart(product);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = () => {
    navigate(`/product-detail-page?id=${product.id}`);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    onQuickView(product);
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="Star" size={12} className="text-warning fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-border" />
      );
    }

    return stars;
  };

  return (
    <div 
      className="bg-surface rounded-card shadow-card hover:shadow-modal transition-smooth cursor-pointer group"
      onClick={handleCardClick}
    >
      <div className="relative overflow-hidden rounded-t-card">
        <Image
          src={product.image}
          alt={product.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-layout"
        />
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col space-y-1">
          {product.isNew && (
            <span className="bg-accent text-accent-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
              New
            </span>
          )}
          {product.isOnSale && (
            <span className="bg-error text-error-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
              Sale
            </span>
          )}
          {product.isOrganic && (
            <span className="bg-success text-success-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
              Organic
            </span>
          )}
        </div>

        {/* Quick View Button */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-smooth">
          <Button
            variant="ghost"
            onClick={handleQuickView}
            className="p-2 bg-surface bg-opacity-90 hover:bg-opacity-100 rounded-full"
            iconName="Eye"
            iconSize={16}
          />
        </div>

        {/* Stock Status */}
        {product.stock <= 5 && product.stock > 0 && (
          <div className="absolute bottom-2 left-2">
            <span className="bg-warning text-warning-foreground text-xs font-data font-data-medium px-2 py-1 rounded-input">
              Only {product.stock} left
            </span>
          </div>
        )}
        
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="bg-error text-error-foreground text-sm font-body-semibold px-3 py-1 rounded-input">
              Out of Stock
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="mb-2">
          <h3 className="font-body-semibold text-text-primary line-clamp-2 mb-1">
            {product.name}
          </h3>
          <p className="text-sm text-text-secondary">
            {product.unit}
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center space-x-0.5">
            {renderRatingStars(product.rating)}
          </div>
          <span className="text-xs text-text-secondary">
            ({product.reviewCount})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="font-data font-data-medium text-lg text-primary">
              ${product.price.toFixed(2)}
            </span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="font-data text-sm text-text-secondary line-through">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="text-xs font-data font-data-medium text-error">
              {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
            </span>
          )}
        </div>

        {/* Add to Cart Button */}
        <Button
          variant={product.stock === 0 ? "outline" : "primary"}
          onClick={handleAddToCart}
          disabled={product.stock === 0 || isLoading}
          loading={isLoading}
          fullWidth
          iconName={product.stock === 0 ? "AlertCircle" : "ShoppingCart"}
          iconSize={16}
        >
          {product.stock === 0 ? "Out of Stock" : "Add to Cart"}
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;